import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import type { AlwaysAiUser } from "../../../shared/api-types";
import { AppointmentScopeSelector } from "@/components/appointments/appointment-scope-selector";
import { NotFound } from "@/components/not-found";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import {
  ClientThreads,
  CHOOSE_RD_MESSAGES_MESSAGE,
  MessageList,
  MessagesHeading,
  THREAD_NOT_AVAILABLE_MESSAGE,
  UNAVAILABLE_MESSAGES_MESSAGE,
  type MessageThreadLinkProps,
} from "@/components/v2/message-parts";
import { PatientAvatar } from "@/components/v2/patient-list-parts";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { useShellFrame } from "@/components/v2/shell-layout";
import { authenticatedApi } from "@/lib/api";
import { appointmentScopeFor } from "@/lib/appointment-scope-state";
import { deviceTimezone } from "@/lib/appointments";
import {
  messageListView,
  messagesPageState,
  pollFailureView,
  threadPageView,
  threadsPageKey,
  type MessagesSearch,
} from "@/lib/messages-client";
import { patientName } from "@/lib/patients-client";
import { can, canSelectAppointmentRd } from "@/lib/permissions";
import { PATIENT_MESSAGING_FLAG, useFeatures } from "@/lib/use-features";
import {
  pollStateOf,
  useClientThreads,
  useMessageList,
  useOlderMessages,
} from "@/lib/use-messages";
import { useViewedRdName } from "@/lib/use-viewed-rd-name";

// /messages and /messages/$patientId (NO-925): the RD's conversations with
// their Clients, read only, in the single-rail frame and behind
// `patient-messaging`. Blank while the flags and the viewer load; the router's
// 404 for anyone the rail would not show the Messages row to. An admin with
// appointments:read:all picks an RD in the title (`?rdId=`) and reads that RD's
// threads; with nobody picked the page says so, like Appointments.

const PAGE_FRAME = "mx-auto max-w-[880px] px-6 pb-12 pt-8";
const NEUTRAL_MESSAGE = "mt-6 text-body text-muted-foreground";
const PAGE_LINK =
  "shrink-0 text-body font-semibold text-foreground underline underline-offset-4";

function useCurrentUser() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
}

/** Renders the gate's answer, and `children` only once the page may show. */
function MessagesGate({ children }: { children: ReactNode }): ReactElement | null {
  const frame = useShellFrame();
  const features = useFeatures();
  const me = useCurrentUser();
  const user = me.data?.user;
  const state = messagesPageState({
    frame,
    featuresLoading: features.isPending || me.isPending,
    messagingEnabled: features.data?.data[PATIENT_MESSAGING_FLAG] === true,
    canReadAppointments: user
      ? can(user, "appointments:read:own") || canSelectAppointmentRd(user)
      : false,
  });

  if (state === "blank") return null;

  if (state === "not_found") return <NotFound />;

  return <>{children}</>;
}

export function MessagesPage(): ReactElement | null {
  return (
    <MessagesGate>
      <MessagesContent />
    </MessagesGate>
  );
}

export function MessageThreadPage(): ReactElement | null {
  return (
    <MessagesGate>
      <MessageThreadContent />
    </MessagesGate>
  );
}

/** The RD picker as the title, for a viewer who holds appointments:read:all. */
function ScopePicker({
  user,
  search,
  onSelectRd,
}: {
  user: AlwaysAiUser;
  search: MessagesSearch;
  onSelectRd: (rdId: string | null) => void;
}): ReactElement {
  const rdName = useViewedRdName(search.rdId);
  const holdsOwnRows = can(user, "appointments:read:own");

  return (
    <AppointmentScopeSelector
      scope={appointmentScopeFor(search.rdId, holdsOwnRows)}
      rdName={rdName}
      holdsOwnRows={holdsOwnRows}
      ownLabel="My Messages"
      onSelectRd={onSelectRd}
    />
  );
}

function renderThreadLink(search: MessagesSearch) {
  return function ThreadLink({
    patientId,
    className,
    dataAttributes,
    children,
  }: MessageThreadLinkProps): ReactElement {
    return (
      <Link
        to="/messages/$patientId"
        params={{ patientId }}
        search={search}
        className={className}
        {...dataAttributes}
      >
        {children}
      </Link>
    );
  };
}

function MessagesContent(): ReactElement {
  const search = useSearch({ strict: false }) as MessagesSearch;
  const navigate = useNavigate();
  const list = useMessageList(search.rdId);
  const failure = pollFailureView(pollStateOf(list));
  const [now] = useState(() => new Date());
  const user = useCurrentUser().data?.user;
  const picker =
    user && canSelectAppointmentRd(user) ? (
      <ScopePicker
        user={user}
        search={search}
        onSelectRd={(rdId) =>
          void navigate({ to: "/messages", search: rdId ? { rdId } : {} })
        }
      />
    ) : undefined;

  return (
    <div className={PAGE_FRAME}>
      <BreadcrumbSetter segments={[{ label: "Messages" }]} />
      <MessagesHeading picker={picker} />
      {failure === "error_state" ? (
        <QueryErrorState
          error={list.error}
          notFoundText="Messages are not available"
          onRetry={() => void list.refetch()}
          className="mt-10"
        />
      ) : list.data ? (
        <>
          <RefreshFailedHint show={failure === "stale_hint"} />
          <MessageList
            view={messageListView(list.data)}
            timezone={deviceTimezone()}
            now={now}
            renderThreadLink={renderThreadLink(search)}
          />
        </>
      ) : null}
    </div>
  );
}

function MessageThreadContent(): ReactElement {
  const { patientId } = useParams({ from: "/workspace/messages/$patientId" });
  const search = useSearch({ strict: false }) as MessagesSearch;
  const thread = useClientThreads(patientId, search.rdId);
  const firstPageKey =
    thread.data?.status === "ok" ? threadsPageKey(thread.data.threads) : "";
  const olderMessages = useOlderMessages(patientId, search.rdId, firstPageKey);
  const failure = pollFailureView(pollStateOf(thread));
  const [now] = useState(() => new Date());

  return (
    <div className={PAGE_FRAME}>
      <BreadcrumbSetter segments={[{ label: "Messages" }]} />
      {failure === "error_state" ? (
        <ThreadMessage search={search}>
          <QueryErrorState
            error={thread.error}
            notFoundText={THREAD_NOT_AVAILABLE_MESSAGE}
            onRetry={() => void thread.refetch()}
            className="mt-6"
          />
        </ThreadMessage>
      ) : thread.data ? (
        <>
          <RefreshFailedHint show={failure === "stale_hint"} />
          <ThreadBody
            patientId={patientId}
            search={search}
            view={threadPageView(thread.data)}
            older={olderMessages}
            now={now}
          />
        </>
      ) : null}
    </div>
  );
}

/** A background refresh failed; the last messages stay on screen and the next poll tries again. */
function RefreshFailedHint({ show }: { show: boolean }): ReactElement | null {
  if (!show) return null;

  return (
    <p role="status" className={NEUTRAL_MESSAGE}>
      Couldn&apos;t refresh just now. Showing the last messages we loaded.
    </p>
  );
}

function BackToMessages({ search }: { search: MessagesSearch }): ReactElement {
  return (
    <Link to="/messages" search={search} className={PAGE_LINK}>
      ← Messages
    </Link>
  );
}

function ThreadMessage({
  search,
  children,
}: {
  search: MessagesSearch;
  children: ReactNode;
}): ReactElement {
  return (
    <>
      <BackToMessages search={search} />
      <div>{children}</div>
    </>
  );
}

function ThreadBody({
  patientId,
  search,
  view,
  older,
  now,
}: {
  patientId: string;
  search: MessagesSearch;
  view: ReturnType<typeof threadPageView>;
  older: ReturnType<typeof useOlderMessages>;
  now: Date;
}): ReactElement {
  if (view.kind === "no_selection") {
    return (
      <ThreadMessage search={search}>
        <p className={NEUTRAL_MESSAGE}>{CHOOSE_RD_MESSAGES_MESSAGE}</p>
      </ThreadMessage>
    );
  }

  if (view.kind === "unavailable") {
    return (
      <ThreadMessage search={search}>
        <p className={NEUTRAL_MESSAGE}>{UNAVAILABLE_MESSAGES_MESSAGE}</p>
      </ThreadMessage>
    );
  }

  const client = { ...view.client, id: patientId, initials: null };

  return (
    <>
      <header className="flex items-center gap-4">
        <PatientAvatar patient={client} size="header" />
        <h1
          data-patient-name
          className="min-w-0 flex-1 truncate font-sans text-3xl font-bold tracking-tight text-foreground brand:text-display brand:leading-[1.15]"
        >
          {patientName(client)}
        </h1>
        <BackToMessages search={search} />
      </header>
      <ClientThreads
        threads={view.threads}
        older={older.older}
        onLoadOlder={(threadId, cursor) => void older.load(threadId, cursor)}
        timezone={deviceTimezone()}
        now={now}
      />
    </>
  );
}
